<template>
  <div class="financial-monthly-report">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator-class="el-icon-arrow-right" style="margin-bottom: 20px;">
      <el-breadcrumb-item :to="{ path: '/' }">结算管理</el-breadcrumb-item>
      <el-breadcrumb-item>财务月报</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <el-select
        v-model="selectedYear"
        placeholder="选择年份"
        @change="loadData"
        style="width: 120px; margin-right: 10px;">
        <el-option
          v-for="year in yearOptions"
          :key="year"
          :label="year + '年'"
          :value="year">
        </el-option>
      </el-select>
      <el-button type="primary" icon="el-icon-refresh" @click="handleRefreshCache" :loading="refreshing">
        刷新缓存
      </el-button>
    </div>

    <!-- 数据表格 -->
    <div class="table-container">
      <!-- 表格标题 -->
      <div class="table-title" style="text-align: center; font-size: 18px; font-weight: bold; color: #303133; margin-bottom: 10px; padding: 10px 0;">
        {{ selectedYear }}年财务报表
      </div>
      <el-table :data="tableData" border style="width: 100%" size="small">
        <!-- 行号列 -->
        <el-table-column type="index" width="50" align="center" label="行号"></el-table-column>
        
        <!-- 条目列 -->
        <el-table-column prop="itemName" min-width="130" label="条目"></el-table-column>
        
        <!-- 数据来源列 -->
        <el-table-column prop="dataSource" width="80" label="数据来源" align="center">
          <template slot-scope="scope">
            <el-tag v-if="scope.row.dataSource === '计算'" type="info" size="mini">计算</el-tag>
            <el-tag v-else-if="scope.row.dataSource === '手动'" type="warning" size="mini">手动</el-tag>
            <span v-else></span>
          </template>
        </el-table-column>
        
        <!-- 1-12月列 -->
        <el-table-column v-for="month in 12" :key="month" :label="month + '月'" width="110" align="right">
          <template slot-scope="scope">
            <template v-if="scope.row.editable">
              <span class="editable-cell" style="cursor: pointer;" @click="handleEditClick(scope.row, month)">
                {{ formatAmount(getMonthValue(scope.row, month)) }}
                <i class="el-icon-edit edit-icon"></i>
              </span>
            </template>
            <template v-else>
              <span>
                {{ formatAmount(getMonthValue(scope.row, month)) }}
              </span>
            </template>
          </template>
        </el-table-column>
      </el-table>
      
      <!-- 缓存说明 -->
      <div class="cache-notice" style="margin-top: 15px; padding: 10px 15px; background-color: #f4f4f5; border-left: 4px solid #909399; color: #606266; font-size: 13px; line-height: 1.6;">
        <i class="el-icon-info" style="margin-right: 5px; color: #909399;"></i>
        缓存说明：每日凌晨会统计所有数据，缓存到月报表。即：当前看到的是前一天的缓存。 也可以点击【刷新缓存】重新计算，但是由于数据量大可能计算时间较长。
        <br/>
        <i class="el-icon-info" style="margin-right: 5px; color: #909399;"></i>
        2.1 线上退货退款 = 【已出库售后】页面中"处理办法"为“退款”， 且“问题类别”为“物流漏发或其他错误”或“客户退款退货” 的退款金额总和(台币转人民币)
        <br/>
        <i class="el-icon-info" style="margin-right: 5px; color: #909399;"></i>
        2.3 不取货 = 【已出库售后】页面中"处理办法"为“退款”， 且“问题类别”为“客户未取货” 的退款金额总和(台币转人民币)
        <br/>
        <i class="el-icon-info" style="margin-right: 5px; color: #909399;"></i>
        2.5 物流理赔 = 【物流理赔管理】页面中“处理办法”为“已赔付” 的退款金额总和(台币转人民币)
        <br/>
        <i class="el-icon-info" style="margin-right: 5px; color: #909399;"></i>
        合计 = 1 + 2.5 - (2.1 + 2.2 + 2.3 + 2.4 + 3 + 4 + 5) 
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog :title="editDialogTitle" :visible.sync="editDialogVisible" width="500px">
      <el-form :model="editForm" label-width="120px">
        <el-form-item v-if="editingRow && editingRow.editField === 'onlineRefund'" label="2.2 线上退款">
          <el-input-number v-model="editForm.onlineRefund" :precision="2" :step="100" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item v-if="editingRow && editingRow.editField === 'offlineRefund'" label="2.4 线下退款">
          <el-input-number v-model="editForm.offlineRefund" :precision="2" :step="100" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item v-if="editingRow && editingRow.editField === 'advertisingCost'" label="3. 广告投入">
          <el-input-number v-model="editForm.advertisingCost" :precision="2" :step="100" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item v-if="editingRow && editingRow.editField === 'staffSalary'" label="4. 员工工资">
          <el-input-number v-model="editForm.staffSalary" :precision="2" :step="100" style="width: 100%;"></el-input-number>
        </el-form-item>
        <el-form-item v-if="editingRow && editingRow.editField === 'softwareCost'" label="5. 软件费用">
          <el-input-number v-model="editForm.softwareCost" :precision="2" :step="100" style="width: 100%;"></el-input-number>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="editDialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleEditSubmit">保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { getByYear, updateManualFields, refreshCache } from '@/api/FinancialMonthlyReport'

export default {
  name: 'FinancialMonthlyReport',
  data() {
    const currentYear = new Date().getFullYear()
    const yearOptions = []
    for (let year = 2025; year <= currentYear; year++) {
      yearOptions.push(year)
    }
    
    return {
      selectedYear: currentYear,
      yearOptions,
      yearData: null,
      refreshing: false,
      editDialogVisible: false,
      editForm: {
        id: null,
        onlineRefund: 0,
        offlineRefund: 0,
        advertisingCost: 0,
        staffSalary: 0,
        softwareCost: 0
      },
      editingMonth: null,
      editingRow: null
    }
  },
  computed: {
    editDialogTitle() {
      if (!this.editingRow || !this.editingMonth) {
        return '编辑月份数据'
      }
      return `${this.selectedYear}年${this.editingMonth}月`
    },
    tableData() {
      if (!this.yearData || !this.yearData.months) return []
      
      const months = this.yearData.months
      
      return [
        // 1. 实际利润
        {
          itemName: '1.实际利润',
          dataSource: '计算',
          editable: false,
          field: 'actualProfit',
          monthValues: this.getMonthValues('actualProfit')
        },
        // 2.1 线上退货退款
        {
          itemName: '2.1 线上退货退款',
          dataSource: '计算',
          editable: false,
          field: 'onlineReturnRefund',
          monthValues: this.getMonthValues('onlineReturnRefund')
        },
        // 2.2 线下退款
        {
          itemName: '2.2 线上退款',
          dataSource: '手动',
          editable: true,
          field: 'onlineRefund',
          monthValues: this.getMonthValues('onlineRefund'),
          editField: 'onlineRefund'
        },
        // 2.3 不取货
        {
          itemName: '2.3 不取货',
          dataSource: '计算',
          editable: false,
          field: 'customerNotPickup',
          monthValues: this.getMonthValues('customerNotPickup')
        },
        // 2.4 线下退款
        {
          itemName: '2.4 线下退款',
          dataSource: '手动',
          editable: true,
          field: 'offlineRefund',
          monthValues: this.getMonthValues('offlineRefund'),
          editField: 'offlineRefund'
        },
        // 2.5 物流理赔
        {
          itemName: '2.5 物流理赔',
          dataSource: '计算',
          editable: false,
          field: 'logisticsClaim',
          monthValues: this.getMonthValues('logisticsClaim'),
          isPositive: true
        },
        // 3. 广告投入
        {
          itemName: '3. 广告投入',
          dataSource: '手动',
          editable: true,
          field: 'advertisingCost',
          monthValues: this.getMonthValues('advertisingCost'),
          editField: 'advertisingCost'
        },
        // 4. 员工工资
        {
          itemName: '4. 员工工资',
          dataSource: '手动',
          editable: true,
          field: 'staffSalary',
          monthValues: this.getMonthValues('staffSalary'),
          editField: 'staffSalary'
        },
        // 5. 软件费用
        {
          itemName: '5. 软件费用',
          dataSource: '手动',
          editable: true,
          field: 'softwareCost',
          monthValues: this.getMonthValues('softwareCost'),
          editField: 'softwareCost'
        },
        // 合计
        {
          itemName: '合计',
          dataSource: '计算',
          editable: false,
          field: 'finalProfit',
          monthValues: this.getMonthValues('finalProfit'),
          isSummary: true
        }
      ]
    }
  },
  created() {
    this.loadData()
  },
  methods: {
    async loadData() {
      try {
        const res = await getByYear(this.selectedYear)
        if (res.success) {
          this.yearData = res.data
        }
      } catch (error) {
        this.$message.error('加载数据失败')
        console.error(error)
      }
    },
    
    getMonthValues(field) {
      if (!this.yearData || !this.yearData.months) return {}
      const values = {}
      this.yearData.months.forEach(m => {
        values[m.reportMonth] = m[field]
      })
      return values
    },
    
    getMonthValue(row, month) {
      return row.monthValues && row.monthValues[month] != null ? row.monthValues[month] : 0
    },
    
    formatAmount(amount) {
      if (amount == null) return '-'
      const num = parseFloat(amount)
      return num.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    
    handleEditClick(row, month) {
      if (!row.editable || !row.editField) return
      
      const monthData = this.yearData.months.find(m => m.reportMonth === month)
      if (!monthData || !monthData.id) return
      
      this.editingRow = row
      this.editingMonth = month
      this.editForm = {
        id: monthData.id,
        onlineRefund: monthData.onlineRefund || 0,
        offlineRefund: monthData.offlineRefund || 0,
        advertisingCost: monthData.advertisingCost || 0,
        staffSalary: monthData.staffSalary || 0,
        softwareCost: monthData.softwareCost || 0
      }
      this.editDialogVisible = true
    },
    
    async handleEditSubmit() {
      try {
        const fieldToUpdate = this.editingRow.editField
        const updateData = {
          id: this.editForm.id,
          [fieldToUpdate]: this.editForm[fieldToUpdate]
        }
        await updateManualFields(updateData)
        this.$message.success('保存成功')
        this.editDialogVisible = false
        this.editingRow = null
        this.loadData()
      } catch (error) {
        this.$message.error('保存失败')
        console.error(error)
      }
    },
    
    async handleRefreshCache() {
      this.refreshing = true
      try {
        await refreshCache(this.selectedYear)
        this.$message.success('刷新缓存成功')
        this.loadData()
      } catch (error) {
        this.$message.error('刷新缓存失败')
        console.error(error)
      } finally {
        this.refreshing = false
      }
    }
  }
}
</script>

<style scoped>
.financial-monthly-report {
  padding: 20px;
}

.toolbar {
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.table-container {
  background: #fff;
  padding: 20px;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
}

/* 可编辑单元格样式：默认隐藏铅笔，悬停显示 */
.editable-cell .edit-icon {
  display: none;
  margin-left: 5px;
}

.editable-cell:hover .edit-icon {
  display: inline-block;
}

.el-table >>> .el-table__row.is-group {
  background-color: #f5f7fa;
  font-weight: bold;
}

.el-table >>> .el-table__row.is-summary {
  background-color: #ecf5ff;
  font-weight: bold;
}
</style>



// WEBPACK FOOTER //
// src/views/finance/FinancialMonthlyReport.vue